/**
 * 海报绘制工具函数
 */

import QRCode from 'qrcode';
import type {
  PosterElement,
  ImageElement,
  TextElement,
  LineElement,
  RectElement,
  CircleElement,
  QrcodeElement,
  ShadowConfig,
  GradientConfig,
} from './types';

/**
 * 加载图片
 * 注意：对于跨域图片，需要服务器配置 CORS 头（Access-Control-Allow-Origin）
 * 开发环境：需要在云存储 CORS 配置中添加 http://localhost 和 http://localhost:端口号
 */
export function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    // 判断是否为跨域图片（非同源）
    const isCrossOrigin = (() => {
      try {
        const urlObj = new URL(url, window.location.href);
        return urlObj.origin !== window.location.origin;
      } catch {
        // 如果 URL 解析失败，假设是跨域的
        return true;
      }
    })();
    
    // 对于跨域图片，必须设置 crossOrigin = 'anonymous' 才能在 Canvas 中绘制
    if (isCrossOrigin) {
      img.crossOrigin = 'anonymous';
    }
    
    const timeout = setTimeout(() => {
      img.onload = null;
      img.onerror = null;
      const error = new Error(`图片加载超时: ${url}`);
      reject(error);
    }, 30000); // 30秒超时
    
    img.onload = () => {
      clearTimeout(timeout);
      resolve(img);
    };
    
    img.onerror = (error) => {
      clearTimeout(timeout);
      const errorMessage = isCrossOrigin
        ? `跨域图片加载失败: ${url}。请检查云存储 CORS 配置是否允许当前域名（${window.location.origin}）。开发环境需要添加 http://localhost 和 http://localhost:端口号 到 CORS 允许列表。`
        : `图片加载失败: ${url}`;
      reject(new Error(errorMessage));
    };
    
    img.src = url;
  });
}

/**
 * 应用阴影
 */
export function applyShadow(
  ctx: CanvasRenderingContext2D,
  shadow?: ShadowConfig
) {
  if (!shadow) {
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';
    return;
  }

  ctx.shadowOffsetX = shadow.offsetX;
  ctx.shadowOffsetY = shadow.offsetY;
  ctx.shadowBlur = shadow.blur;
  ctx.shadowColor = shadow.color;
}

/**
 * 应用渐变
 */
export function applyGradient(
  ctx: CanvasRenderingContext2D,
  gradient: GradientConfig,
  x: number,
  y: number,
  width: number,
  height: number
): CanvasGradient {
  const { direction, colors, startX, startY, endX, endY } = gradient;

  let startXPos = startX ?? x;
  let startYPos = startY ?? y;
  let endXPos = endX ?? x + width;
  let endYPos = endY ?? y + height;

  if (direction === 'horizontal') {
    endXPos = x + width;
    endYPos = y;
  } else if (direction === 'vertical') {
    endXPos = x;
    endYPos = y + height;
  } else if (direction === 'diagonal') {
    endXPos = x + width;
    endYPos = y + height;
  }

  const canvasGradient = ctx.createLinearGradient(
    startXPos,
    startYPos,
    endXPos,
    endYPos
  );

  colors.forEach(({ offset, color }) => {
    canvasGradient.addColorStop(offset, color);
  });

  return canvasGradient;
}

/**
 * 绘制圆角矩形路径
 */
export function drawRoundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/**
 * 绘制图片元素
 */
export async function drawImage(
  ctx: CanvasRenderingContext2D,
  element: ImageElement
) {
  const {
    url,
    top,
    left,
    width,
    height,
    borderRadius = 0,
    borderWidth = 0,
    borderColor = '#000',
    opacity = 1,
    fillMode = 'fill',
    shadow,
  } = element;

  ctx.save();

  // 应用透明度
  ctx.globalAlpha = opacity;

  // 应用阴影
  if (shadow) {
    applyShadow(ctx, shadow);
  }

  try {
    const img = await loadImage(url);

    // 判断是否为圆形（当 borderRadius 大于等于宽度和高度的一半时）
    const isCircle = borderRadius > 0 && 
                     borderRadius >= Math.min(width, height) / 2;

    // 如果有圆角或边框，需要裁剪
    if (borderRadius > 0 || borderWidth > 0) {
      ctx.save();
      if (borderRadius > 0) {
        if (isCircle) {
          // 绘制圆形路径
          const centerX = left + width / 2;
          const centerY = top + height / 2;
          const radius = Math.min(width, height) / 2;
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
          ctx.closePath();
        } else {
          // 绘制圆角矩形路径
          drawRoundedRectPath(ctx, left, top, width, height, borderRadius);
        }
        ctx.clip();
      }

      // 根据填充模式绘制图片
      if (fillMode === 'cover') {
        const scale = Math.max(width / img.width, height / img.height);
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        const x = left + (width - scaledWidth) / 2;
        const y = top + (height - scaledHeight) / 2;
        ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
      } else if (fillMode === 'contain') {
        const scale = Math.min(width / img.width, height / img.height);
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        const x = left + (width - scaledWidth) / 2;
        const y = top + (height - scaledHeight) / 2;
        ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
      } else if (fillMode === 'fill') {
        ctx.drawImage(img, left, top, width, height);
      } else {
        // none - 保持原始尺寸
        ctx.drawImage(img, left, top);
      }

      ctx.restore();

      // 绘制边框
      if (borderWidth > 0) {
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = borderWidth;
        if (borderRadius > 0) {
          if (isCircle) {
            // 绘制圆形边框
            const centerX = left + width / 2;
            const centerY = top + height / 2;
            const radius = Math.min(width, height) / 2;
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
            ctx.closePath();
          } else {
            // 绘制圆角矩形边框
            drawRoundedRectPath(ctx, left, top, width, height, borderRadius);
          }
        } else {
          ctx.rect(left, top, width, height);
        }
        ctx.stroke();
      }
    } else {
      // 无圆角无边框，直接绘制
      if (fillMode === 'cover') {
        const scale = Math.max(width / img.width, height / img.height);
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        const x = left + (width - scaledWidth) / 2;
        const y = top + (height - scaledHeight) / 2;
        ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
      } else if (fillMode === 'contain') {
        const scale = Math.min(width / img.width, height / img.height);
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        const x = left + (width - scaledWidth) / 2;
        const y = top + (height - scaledHeight) / 2;
        ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
      } else if (fillMode === 'fill') {
        ctx.drawImage(img, left, top, width, height);
      } else {
        ctx.drawImage(img, left, top);
      }
    }
  } catch (error) {
    // 绘制占位矩形
    ctx.fillStyle = '#f0f0f0';
    if (borderRadius > 0) {
      const isCircle = borderRadius >= Math.min(width, height) / 2;
      if (isCircle) {
        // 绘制圆形占位
        const centerX = left + width / 2;
        const centerY = top + height / 2;
        const radius = Math.min(width, height) / 2;
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.closePath();
        ctx.fill();
      } else {
        drawRoundedRectPath(ctx, left, top, width, height, borderRadius);
        ctx.fill();
      }
    } else {
      ctx.fillRect(left, top, width, height);
    }
  }

  ctx.restore();
}

/**
 * 绘制文字元素
 */
export function drawText(ctx: CanvasRenderingContext2D, element: TextElement) {
  const {
    content,
    top,
    left,
    fontSize = 16,
    color = '#000',
    textAlign = 'left',
    baseLine = 'top',
    opacity = 1,
    width,
    lineNum = 1,
    lineHeight = 0,
    fontWeight = 'normal',
    fontFamily = 'Microsoft YaHei, sans-serif',
    shadow,
    maxWidth,
    ellipsis = false,
  } = element;

  ctx.save();

  // 应用透明度
  ctx.globalAlpha = opacity;

  // 设置字体
  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  ctx.fillStyle = color;
  ctx.textAlign = textAlign;
  ctx.textBaseline = baseLine;

  // 应用阴影
  if (shadow) {
    applyShadow(ctx, shadow);
  }

  // 计算实际行高
  const actualLineHeight = lineHeight || fontSize * 1.2;

  // 处理文字换行
  const textWidth = width || maxWidth || ctx.canvas.width;
  const words = content.split('');
  const lines: string[] = [];
  let currentLine = '';

  for (let i = 0; i < words.length; i++) {
    const testLine = currentLine + words[i];
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;

    if (testWidth > textWidth && currentLine !== '') {
      lines.push(currentLine);
      currentLine = words[i];
    } else {
      currentLine = testLine;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  // 限制行数
  const displayLines = lines.slice(0, lineNum);
  if (ellipsis && lines.length > lineNum) {
    const lastLine = displayLines[displayLines.length - 1];
    const ellipsisText = '...';
    let truncated = lastLine;
    while (
      ctx.measureText(truncated + ellipsisText).width > textWidth &&
      truncated.length > 0
    ) {
      truncated = truncated.slice(0, -1);
    }
    displayLines[displayLines.length - 1] = truncated + ellipsisText;
  }

  // 绘制每一行：left 为锚点横坐标，含义由 textAlign 决定（左缘/中心/右缘）
  displayLines.forEach((line, index) => {
    const x = left;

    // 根据 baseLine 计算 Y 坐标
    let y: number;
    if (baseLine === 'top') {
      // top 基线：y 坐标就是文字的顶部
      // 第一行的顶部位于 top，后续行按行高递增
      y = top + index * actualLineHeight;
    } else if (baseLine === 'bottom') {
      // bottom 基线：y 坐标是文字的底部
      // 最后一行的底部位于 top，从底部向上计算
      y = top - (displayLines.length - 1 - index) * actualLineHeight;
    } else if (baseLine === 'middle') {
      // middle 基线：y 坐标是文字的中心
      // 整体文本的中心位于 top
      const totalHeight = displayLines.length * actualLineHeight;
      const centerY = top - totalHeight / 2;
      y = centerY + index * actualLineHeight + fontSize / 2;
    } else {
      // alphabetic 或其他基线：使用默认逻辑
      // 第一行的基线位于 top，后续行按行高递增
      y = top + index * actualLineHeight + fontSize;
    }
    ctx.fillText(line, x, y);
  });

  ctx.restore();
}

/**
 * 绘制线条元素
 */
export function drawLine(ctx: CanvasRenderingContext2D, element: LineElement) {
  const {
    startX,
    startY,
    endX,
    endY,
    color = '#000',
    width = 1,
    lineCap = 'butt',
    opacity = 1,
    shadow,
  } = element;

  ctx.save();

  ctx.globalAlpha = opacity;
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = lineCap;

  if (shadow) {
    applyShadow(ctx, shadow);
  }

  ctx.beginPath();
  ctx.moveTo(startX, startY);
  ctx.lineTo(endX, endY);
  ctx.stroke();

  ctx.restore();
}

/**
 * 绘制矩形元素
 */
export function drawRect(ctx: CanvasRenderingContext2D, element: RectElement) {
  const {
    width,
    height,
    x,
    y,
    backgroundColor,
    borderWidth = 0,
    borderColor = '#000',
    borderRadius = 0,
    opacity = 1,
    paddingLeft = 0,
    paddingRight = 0,
    paddingTop = 0,
    paddingBottom = 0,
    text,
    gradient,
    shadow,
  } = element;

  ctx.save();

  ctx.globalAlpha = opacity;

  const rectX = x;
  const rectY = y;
  const rectWidth = width;
  const rectHeight = height;

  // 应用阴影
  if (shadow) {
    applyShadow(ctx, shadow);
  }

  // 绘制背景
  if (backgroundColor || gradient) {
    if (gradient) {
      const grad = applyGradient(
        ctx,
        gradient,
        rectX,
        rectY,
        rectWidth,
        rectHeight
      );
      ctx.fillStyle = grad;
    } else {
      ctx.fillStyle = backgroundColor || 'transparent';
    }

    if (borderRadius > 0) {
      drawRoundedRectPath(ctx, rectX, rectY, rectWidth, rectHeight, borderRadius);
      ctx.fill();
    } else {
      ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
    }
  }

  // 绘制边框
  if (borderWidth > 0) {
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = borderWidth;
    if (borderRadius > 0) {
      drawRoundedRectPath(ctx, rectX, rectY, rectWidth, rectHeight, borderRadius);
    } else {
      ctx.rect(rectX, rectY, rectWidth, rectHeight);
    }
    ctx.stroke();
  }

  // 绘制文字
  if (text) {
    const textX = rectX + paddingLeft;
    const textY = rectY + paddingTop;
    const textWidth = rectWidth - paddingLeft - paddingRight;
    const textHeight = rectHeight - paddingTop - paddingBottom;

    drawText(ctx, {
      type: 'text',
      ...text,
      top: textY + textHeight / 2,
      left: textX,
      width: textWidth,
    });
  }

  ctx.restore();
}

/**
 * 绘制圆形元素
 */
export function drawCircle(
  ctx: CanvasRenderingContext2D,
  element: CircleElement
) {
  const {
    x,
    y,
    radius,
    backgroundColor,
    borderWidth = 0,
    borderColor = '#000',
    opacity = 1,
    shadow,
  } = element;

  ctx.save();

  ctx.globalAlpha = opacity;

  if (shadow) {
    applyShadow(ctx, shadow);
  }

  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);

  if (backgroundColor) {
    ctx.fillStyle = backgroundColor;
    ctx.fill();
  }

  if (borderWidth > 0) {
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = borderWidth;
    ctx.stroke();
  }

  ctx.restore();
}

/**
 * 生成二维码（使用 qrcode 库）
 */
export async function drawQrcode(
  ctx: CanvasRenderingContext2D,
  element: QrcodeElement
) {
  const {
    text,
    top,
    left,
    width,
    height,
    background = '#fff',
    foreground = '#000',
    padding = 5,
    errorCorrectionLevel = 'M',
  } = element;

  ctx.save();

  // 绘制背景
  ctx.fillStyle = background;
  ctx.fillRect(left, top, width, height);

  try {
    // 创建临时 canvas 用于生成二维码
    const qrCanvas = document.createElement('canvas');
    const qrSize = Math.min(width, height) - padding * 2;
    
    // 使用 qrcode 库生成二维码
    await QRCode.toCanvas(qrCanvas, text, {
      width: qrSize,
      margin: 1,
      color: {
        dark: foreground,
        light: background,
      },
      errorCorrectionLevel: errorCorrectionLevel,
    });

    // 将二维码绘制到目标 canvas 上
    const qrX = left + padding;
    const qrY = top + padding;
    ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);
  } catch (error) {
    // 如果生成失败，绘制一个占位矩形
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(left, top, width, height);
    ctx.strokeStyle = '#ccc';
    ctx.lineWidth = 2;
    ctx.strokeRect(left, top, width, height);
    ctx.fillStyle = '#999';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('二维码生成失败', left + width / 2, top + height / 2);
  }

  ctx.restore();
}

/**
 * 绘制单个元素
 */
export async function drawElement(
  ctx: CanvasRenderingContext2D,
  element: PosterElement
): Promise<void> {
  switch (element.type) {
    case 'image':
      await drawImage(ctx, element);
      break;
    case 'text':
      drawText(ctx, element);
      break;
    case 'line':
      drawLine(ctx, element);
      break;
    case 'rect':
      drawRect(ctx, element);
      break;
    case 'circle':
      drawCircle(ctx, element);
      break;
    case 'qrcode':
      await drawQrcode(ctx, element);
      break;
  }
}
